import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Gift } from "lucide-react";

export default function AuthDialog() {
  const { authOpen, setAuthOpen, login, register } = useAuth();
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      setAuthOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(regForm.name, regForm.email, regForm.password);
      toast.success("Account created!");
      setAuthOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/saved";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="sm:max-w-md bg-white border-brand-cream2" data-testid="auth-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 justify-center mb-1">
            <span className="w-9 h-9 rounded-full bg-brand-green flex items-center justify-center">
              <Gift className="w-5 h-5 text-brand-cream" />
            </span>
          </div>
          <DialogTitle className="text-center font-serif text-2xl">Welcome to Choicefully</DialogTitle>
          <p className="text-center text-sm text-muted-foreground">Save recipients & revisit your searches.</p>
        </DialogHeader>

        <Button
          variant="outline"
          onClick={googleLogin}
          data-testid="auth-google-button"
          className="w-full border-brand-cream2 hover:bg-brand-cream2 h-11 gap-2"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4" />
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-1">
          <div className="h-px flex-1 bg-brand-cream2" />
          <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
          <div className="h-px flex-1 bg-brand-cream2" />
        </div>

        <Tabs defaultValue="login">
          <TabsList className="grid grid-cols-2 w-full bg-brand-cream2">
            <TabsTrigger value="login" data-testid="auth-tab-login">Log in</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Register</TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-3 mt-3">
              <div>
                <Label htmlFor="li-email">Email</Label>
                <Input id="li-email" type="email" required data-testid="login-email-input"
                  value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="li-pw">Password</Label>
                <Input id="li-pw" type="password" required data-testid="login-password-input"
                  value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
              </div>
              <Button type="submit" disabled={busy} data-testid="auth-login-submit-button"
                className="w-full bg-brand-green hover:bg-brand-greendark h-11">
                {busy ? "Signing in…" : "Log in"}
              </Button>
            </form>
          </TabsContent>
          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-3 mt-3">
              <div>
                <Label htmlFor="rg-name">Name</Label>
                <Input id="rg-name" required data-testid="register-name-input"
                  value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="rg-email">Email</Label>
                <Input id="rg-email" type="email" required data-testid="register-email-input"
                  value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="rg-pw">Password</Label>
                <Input id="rg-pw" type="password" required minLength={6} data-testid="register-password-input"
                  value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />
              </div>
              <Button type="submit" disabled={busy} data-testid="auth-register-submit-button"
                className="w-full bg-brand-green hover:bg-brand-greendark h-11">
                {busy ? "Creating…" : "Create account"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
